/**
 * Kanban Tasks Hook
 *
 * Fetches tasks for all kanban columns in parallel using useQueries.
 *
 * @module features/tasks/hooks/useKanbanTasks
 */

import { useQueries } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import type { AgentTask, AgentTaskListResponse, AgentTaskStatus } from '../types';
import { KANBAN_COLUMNS } from '../constants';

/** Prefix shared by every kanban column query (and other /tasks lists). */
export const TASKS_LIST_PREFIX = ['tasks'] as const;

/**
 * Manual kanban order: position asc (NULLs last server-side), so drops stay
 * where the user put them instead of re-sorting by priority/createdAt.
 */
const KANBAN_SORT = 'position:asc';

/**
 * Resolves a cached /tasks query key to the status column it holds, or null
 * for non-column keys (e.g. task detail). Used by the optimistic kanban move
 * to find the source/target column caches across all filter variants.
 */
export function kanbanColumnOfKey(key: QueryKey): AgentTaskStatus | null {
  if (key[0] !== 'tasks') return null;
  const params = key[1];
  if (!params || typeof params !== 'object' || !('status' in params)) return null;
  const status = (params as { status: unknown }).status;
  return typeof status === 'string' ? (status as AgentTaskStatus) : null;
}

export interface KanbanColumnData {
  tasks: AgentTask[];
  isLoading: boolean;
  totalItems: number;
}

interface KanbanTasksOptions {
  completedUpdatedAfter?: string;
  search?: string;
  agentSubmitted?: 'true' | 'false';
  tagIds?: string[];
  /** Exclude tasks carrying any of these tag ids (negative tag filter). */
  excludeTagIds?: string[];
  /** Filter to these priority levels (0=Normal, 1=High, 2=Critical). */
  priorities?: number[];
  /** Filter to tasks linked to this Account board card (customer). */
  accountCardId?: string;
  /** Filter to tasks created by this user (the submitter). */
  createdBy?: string;
}

export function useKanbanTasks(
  options?: KanbanTasksOptions
): Record<AgentTaskStatus, KanbanColumnData> {
  const {
    completedUpdatedAfter,
    search,
    agentSubmitted,
    tagIds,
    excludeTagIds,
    priorities,
    accountCardId,
    createdBy,
  } = options ?? {};
  const searchText = search?.trim();
  const excludeTag = agentSubmitted === 'true' ? 'auto-filed' : undefined;
  const selectedTagIds = tagIds?.filter((tagId) => tagId.trim().length > 0) ?? [];
  const selectedExcludeTagIds = excludeTagIds?.filter((tagId) => tagId.trim().length > 0) ?? [];
  const selectedPriorities = priorities ?? [];

  const results = useQueries({
    queries: KANBAN_COLUMNS.map((col) => {
      const updatedAfter = col.id === 'completed' ? completedUpdatedAfter : undefined;
      return {
        queryKey: [
          'tasks',
          {
            status: col.id,
            pageSize: 100,
            sort: KANBAN_SORT,
            updatedAfter,
            search: searchText,
            agentSubmitted,
            excludeTag,
            tagIds: selectedTagIds,
            excludeTagIds: selectedExcludeTagIds,
            priorities: selectedPriorities,
            accountCardId,
            createdBy,
          },
        ],
        queryFn: async (): Promise<AgentTaskListResponse> => {
          const params = new URLSearchParams({
            status: col.id,
            pageSize: '100',
            page: '1',
            sort: KANBAN_SORT,
          });
          if (updatedAfter) params.set('updatedAfter', updatedAfter);
          if (searchText) params.set('search', searchText);
          if (agentSubmitted) params.set('agentSubmitted', agentSubmitted);
          if (excludeTag) params.set('excludeTag', excludeTag);
          if (selectedTagIds.length > 0) params.set('tagIds', selectedTagIds.join(','));
          if (selectedExcludeTagIds.length > 0)
            params.set('excludeTagIds', selectedExcludeTagIds.join(','));
          if (selectedPriorities.length > 0) params.set('priority', selectedPriorities.join(','));
          if (accountCardId) params.set('accountCardId', accountCardId);
          if (createdBy) params.set('createdBy', createdBy);
          const res = await fetch(`${API_URL}/tasks?${params}`, {
            credentials: 'include',
          });
          if (!res.ok) throw await toApiError(res, `Failed to fetch ${col.id} tasks`);
          return res.json();
        },
      };
    }),
  });

  const columnData = {} as Record<AgentTaskStatus, KanbanColumnData>;

  KANBAN_COLUMNS.forEach((col, i) => {
    const result = results[i];
    columnData[col.id] = {
      tasks: result.data?.data ?? [],
      isLoading: result.isLoading,
      totalItems: result.data?.pagination?.totalItems ?? 0,
    };
  });

  return columnData;
}
